import React from 'react';
import { Container, Box, Typography, useTheme } from '@mui/material';
import SvgIcon from 'src/components/LandingPage/components/SvgIcon';

type Props = {
  title?: string;
  backgroundColor?: string;
};

interface HowItWorksCard {
  iconUrl: string;
  title: string;
  description: string;
}

const S3Bucket = 'https://territory-assets.s3.us-east-1.amazonaws.com/icons';
const cards: HowItWorksCard[] = [
  {
    iconUrl: `${S3Bucket}/shipment-sign.svg`,
    title: 'Find your perfect meals',
    description:
      'Enter your zip code to see meals available in your area. Use our advanced filters to match your dietary needs.',
  },
  {
    iconUrl: `${S3Bucket}/food-delivery-order.svg`,
    title: 'Select your favorites',
    description:
      'Add meals to your cart for the week. Choose one-time delivery or subscribe to save 5%.',
  },
  {
    iconUrl: `${S3Bucket}/delivery-person.svg`,
    title: 'Delivered to your door',
    description:
      'Our chef partners prepare your meals and we deliver them. Track your order every step of the way.',
  },
  {
    iconUrl: `${S3Bucket}/hot-food.svg`,
    title: 'Ready in minutes',
    description: 'Store in your fridge and heat most meals in 2-3 minutes.',
  },
];

/*
 * Reusable component for both the /welcome How It Works slice
 * and the get-started How It Works section.
 */
const HowItWorks: React.FC<Props> = ({
  title = 'How it Works',
  backgroundColor = 'inherit',
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ backgroundColor, py: 10 }}>
      <Container maxWidth={false} sx={{ maxWidth: '1312px', px: 2 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '32px', md: '48px' },
            fontWeight: 600,
            textAlign: { xs: 'left', md: 'center' },
            mb: 6,
            px: { xs: 2, md: 0 },
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'stretch' },
            gap: 3,
          }}
        >
          {cards.map((card, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                minHeight: { xs: '320px', md: '380px' },
                width: { xs: '100%', md: 'calc((100% - 96px) / 4)' },
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E5E5',
                borderRadius: '16px',
                padding: '32px 16px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#FFFFFF',
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>

              <Box
                sx={{
                  height: { xs: '140px', md: '160px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SvgIcon
                    svgUrl={card.iconUrl}
                    color={theme.palette.primary.contrastText}
                    size={40}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#000000',
                    textAlign: 'center',
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '16px',
                    color: '#666666',
                    textAlign: 'center',
                    mt: 1,
                  }}
                >
                  {card.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
